/**
 * Parse API error responses into user-friendly messages
 * Handles multiple error formats from the backend
 */

/**
 * Parse error response and return user-friendly message
 * @param {Error} error - Axios error object
 * @param {string} defaultMessage - Fallback message
 * @returns {string} User-friendly error message
 */
export const parseApiError = (error, defaultMessage = 'An error occurred') => {
  // No response - network error
  if (!error.response) {
    if (error.request) {
      return 'Unable to connect to server. Please check your internet connection.';
    }
    return error.message || defaultMessage;
  }

  const { status, data } = error.response;

  // Handle different status codes
  if (status >= 500) {
    return 'Server error. Please try again later or contact support.';
  }

  if (status === 404) {
    return 'Resource not found.';
  }

  if (status === 403) {
    return 'Access forbidden. You do not have permission to perform this action.';
  }

  // Parse detail field (most common format)
  if (data?.detail) {
    // Format 1: Simple string
    if (typeof data.detail === 'string') {
      return data.detail;
    }

    // Format 2: Validation error array
    // Example: [{ loc: ["body", "email"], msg: "field required", type: "value_error.missing" }]
    if (Array.isArray(data.detail)) {
      const messages = data.detail.map(err => {
        // Extract field name from location array
        const field = err.loc && err.loc.length > 1 
          ? err.loc[err.loc.length - 1] 
          : 'field';
        
        // Format field name (snake_case -> Title Case)
        const fieldName = formatFieldName(field);
        
        // Return formatted message
        return `${fieldName}: ${err.msg}`;
      });

      // Join multiple validation errors
      return messages.join('. ');
    }
  }

  // Fallback to message field
  if (data?.message) {
    return data.message;
  }

  // Last resort: use default
  return defaultMessage;
};

/**
 * Format field name from snake_case to Title Case
 * @param {string} field - Field name in snake_case
 * @returns {string} Formatted field name
 */
const formatFieldName = (field) => {
  return field
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Check if error is authentication-related (401)
 * @param {Error} error - Axios error object
 * @returns {boolean}
 */
export const isAuthError = (error) => {
  return error.response?.status === 401;
};

/**
 * Check if error is conflict (409) - e.g., email already exists
 * @param {Error} error - Axios error object
 * @returns {boolean}
 */
export const isConflictError = (error) => {
  return error.response?.status === 409;
};

/**
 * Check if error is validation error (400/422)
 * @param {Error} error - Axios error object
 * @returns {boolean}
 */
export const isValidationError = (error) => {
  const status = error.response?.status;
  return status === 400 || status === 422;
};

/**
 * Check if error is network-related (no response from server)
 * @param {Error} error - Axios error object
 * @returns {boolean}
 */
export const isNetworkError = (error) => {
  return !error.response && error.request;
};
